import React, { useCallback, memo, useEffect, useRef } from 'react'
import { StyleSheet, View, Platform, Pressable } from 'react-native'
import * as Clipboard from 'expo-clipboard'

import { DS } from '@/design-system'
import { useToastStore } from '@/navigation/toast'
import { useStore } from '@/models'
import { isWeb } from '@/components/WebWrapper'
import { useRouter } from 'expo-router'
import { SocialIcons } from './SocialIcons'

export interface ShareCodeSheetProps {
  code: string
  onClose?: () => void
  showQRButton?: boolean
}

/**
 * ShareCodeSheet - Component for sharing Convex game codes
 *
 * Features:
 * - Prominent code display
 * - Copy to clipboard with toast confirmation
 * - Native share (mobile) or social share buttons (web)
 * - QR code navigation option
 */
export const ShareCodeSheet = memo(({ code, onClose, showQRButton = true }: ShareCodeSheetProps) => {
  const router = useRouter()
  const { share } = useStore()

  // Default URL for social icons (web only, so no prompt needed)
  const shareUrl = share.info?.url ?? `https://app.pokerpower.com/game/join/${code}`
  const shareMessage = `${code}: Join my game on Poker Power`

  const handleCopyLink = useCallback(async () => {
    // In __DEV__, prompt user to choose web or mobile URL
    const { promptShareUrl } = await import('../../utils/shareUrl')
    const url = await promptShareUrl(code)
    await Clipboard.setStringAsync(url)
    useToastStore.getState().enqueueToast({
      kind: 'success',
      message: 'Link copied to clipboard!'
    })
  }, [code])

  const handleCopyCode = useCallback(async () => {
    await Clipboard.setStringAsync(code)
    useToastStore.getState().enqueueToast({
      kind: 'success',
      message: `Code copied: ${code}`
    })
  }, [code])

  const handleNativeShare = useCallback(async () => {
    // In __DEV__, prompt user to choose web or mobile URL
    const { promptShareUrl } = await import('../../utils/shareUrl')
    const url = await promptShareUrl(code)
    await share.promptShare(shareMessage, shareMessage, url)
  }, [share, shareMessage, code])

  // Auto-trigger native share when component mounts/focuses (mobile only)
  const hasTriggeredShareRef = useRef(false)
  useEffect(() => {
    if (Platform.OS !== 'web' && !hasTriggeredShareRef.current) {
      hasTriggeredShareRef.current = true
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        handleNativeShare()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [handleNativeShare])

  const handleShowQR = useCallback(async () => {
    // In __DEV__, prompt user to choose web or mobile URL
    const { promptShareUrl } = await import('../../utils/shareUrl')
    const url = await promptShareUrl(code)
    router.push(`/lobby/qr-code?url=${encodeURIComponent(url)}`)
    onClose?.()
  }, [router, code, onClose])

  return (
    <View style={styles.container}>
      {/* Header */}
      <DS.Frame style={styles.header}>
        <DS.Text variant="Heading 3/Medium">Share Game Code</DS.Text>
        {onClose && (
          <Pressable onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <DS.Icon name="Close" size={24} color="Global/Black" />
          </Pressable>
        )}
      </DS.Frame>

      {/* Code Display */}
      <Pressable
        style={({ pressed }) => [styles.codeContainer, pressed && styles.buttonPressed]}
        onPress={handleCopyCode}
      >
        <DS.Text variant="Heading 2/Medium" color="Brand/Maroon" style={styles.codeText}>
          {code}
        </DS.Text>
        <DS.Frame style={styles.copyBadge} row alignItems="center" gap="4">
          <DS.Icon name="Copy" size={14} color="Brand/Dark Grey" />
          <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
            Copy code
          </DS.Text>
        </DS.Frame>
      </Pressable>

      {/* Action Buttons */}
      <DS.Frame gap="12" style={styles.actionsContainer}>
        {Platform.OS !== 'web' ? (
          <Pressable
            onPress={handleNativeShare}
            style={({ pressed }) => [styles.mobilePrimaryButton, pressed && styles.buttonPressed]}
          >
            <DS.Text variant="Body Medium/Medium" textAlign="center" style={styles.mobilePrimaryButtonText}>
              Share Invite
            </DS.Text>
          </Pressable>
        ) : (
          <DS.Button variant="Large/Primary" type="On Light" onPress={handleCopyLink} maxWidth>
            Copy Link
          </DS.Button>
        )}

        {/* QR Code Button */}
        {showQRButton &&
          (Platform.OS !== 'web' ? (
            <Pressable
              onPress={handleShowQR}
              style={({ pressed }) => [styles.mobileSecondaryButton, pressed && styles.buttonPressed]}
            >
              <DS.Text variant="Body Medium/Medium" textAlign="center" style={styles.mobileSecondaryButtonText}>
                Show QR Code
              </DS.Text>
            </Pressable>
          ) : (
            <DS.Button variant="Large/Secondary" type="On Dark" onPress={handleShowQR} maxWidth>
              Show QR Code
            </DS.Button>
          ))}
      </DS.Frame>

      {/* Social Share Icons (Web only) */}
      {isWeb && (
        <DS.Frame style={styles.socialSection}>
          <DS.Text variant="Body Small/Medium" color="Brand/Dark Grey" textAlign="center">
            Or share via
          </DS.Text>
          <SocialIcons title={shareMessage} shareUrl={shareUrl} />
        </DS.Frame>
      )}
    </View>
  )
})

ShareCodeSheet.displayName = 'ShareCodeSheet'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 32,
    gap: 20,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  codeContainer: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    gap: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2
  },
  codeText: {
    letterSpacing: 4
  },
  copyBadge: {
    marginTop: 2
  },
  actionsContainer: {
    width: '100%'
  },
  mobilePrimaryButton: {
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: 48,
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center'
  },
  mobileSecondaryButton: {
    backgroundColor: 'transparent',
    borderRadius: 48,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center'
  },
  mobilePrimaryButtonText: {
    color: DS.palette['Global/White']
  },
  mobileSecondaryButtonText: {
    color: DS.palette['Global/Black']
  },
  buttonPressed: {
    opacity: 0.7
  },
  socialSection: {
    gap: 12,
    paddingTop: 8
  }
})

export default ShareCodeSheet
